import { FC, useState, useCallback } from 'react'
import { notification } from 'antd'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { SerializedError } from '@reduxjs/toolkit'
import useFormProcessor from '@hooks/useFormProcessor'
import {
  usePostCourseThemeMutation,
  usePutCourseThemeMutation,
} from '@services/courseThemes'
import { usePostScormMutation, usePutScormMutation } from '@services/scorms'
import {
  ControlQuestion,
  CommonCourseThemeBody,
  UpdateCourseThemeBody,
} from '@models/courseTheme'
import { CourseThemeType } from '@models/myCourse'
import AddNewItemModal from '@templates/AddNewItemModal'
import { parseFormHoursToMinutes } from '@utils/themeHoursTime'
import useThemeFormFields from './useThemeFormFields'
import getDefaultValues from './getDefaultValues'
import { ThemeFormValues, ThemeFormModalProps } from './types'
import { InstructionList, ThemeFormModalFrame } from './styled'
import ControlQuestionBlock from './ControlQuestionBlock'

const getTypeFromFile = (
  file: { originalName?: string } | null | undefined
): CourseThemeType => {
  if (!file?.originalName) return 'pdf'
  return file.originalName.toLowerCase().endsWith('.pdf') ? 'pdf' : 'scorm'
}

type ScormUploadResult = {
  scormThemeIri?: string
}

type ScormUploadParams = {
  form: ThemeFormValues
  fileIri: string
  isEdit: boolean
  scormId?: string
  courseIri?: string
  postScorm: (body: {
    title: string
    file: string
    course: string
    type: CourseThemeType
  }) => {
    unwrap: () => Promise<{ '@id': string }>
  }
  putScorm: (body: {
    id: string
    title: string
    file: string
    type: CourseThemeType
  }) => {
    unwrap: () => Promise<{ '@id': string }>
  }
}

const uploadScormAndGetTheme = async (
  params: ScormUploadParams
): Promise<ScormUploadResult> => {
  if (params.isEdit && params.scormId) {
    const scormResult = await params.putScorm({
      id: params.scormId,
      title: params.form.title,
      file: params.fileIri,
      type: getTypeFromFile(params.form.file),
    }).unwrap()

    return { scormThemeIri: scormResult['@id'] }
  }

  if (!params.isEdit && params.courseIri) {
    const scormResult = await params.postScorm({
      title: params.form.title,
      file: params.fileIri,
      course: params.courseIri,
      type: getTypeFromFile(params.form.file),
    }).unwrap()

    return { scormThemeIri: scormResult['@id'] }
  }

  return {}
}

const resolveInternalCourseThemeTest = (
  form: ThemeFormValues,
  internalTheme?: ThemeFormModalProps['internalTheme']
): boolean =>
  getTypeFromFile(form.file) === 'pdf'
    ? false
    : (form.test ?? internalTheme?.test ?? false)

const buildControlQuestion = (
  cq: ControlQuestion | undefined | null
): ControlQuestion | null => {
  if (!cq?.question?.trim()) return null
  const filledOptions = cq.options.filter((o) => o.text.trim())
  if (filledOptions.length === 0) return null
  return {
    question: cq.question.trim(),
    options: filledOptions.map((o) => ({
      text: o.text.trim(),
      isCorrect: o.isCorrect,
    })),
  }
}

const buildNormalizedControlQuestion = (
  cq: ControlQuestion | undefined | null
): ControlQuestion | null => buildControlQuestion(cq)

type BuildInternalPayloadParams = {
  form: ThemeFormValues
  chapterIri: string
  internalTheme?: ThemeFormModalProps['internalTheme']
  isEdit: boolean
}

const buildInternalCourseThemePayload = ({
  form,
  chapterIri,
  internalTheme,
  isEdit,
}: BuildInternalPayloadParams): UpdateCourseThemeBody | CommonCourseThemeBody => {
  const controlQuestion = buildNormalizedControlQuestion(form.controlQuestion)

  if (isEdit) {
    const courseThemeIri = internalTheme?.courseTheme?.['@id']
    if (!courseThemeIri) {
      throw new Error('course theme id is missing')
    }

    const payload: UpdateCourseThemeBody = {
      id: internalTheme.id,
      title: form.title,
      courseChapter: chapterIri,
      test: resolveInternalCourseThemeTest(form, internalTheme),
      hoursTime: parseFormHoursToMinutes(form.hoursTime),
      courseTheme: courseThemeIri,
      ...(controlQuestion === undefined ? {} : { controlQuestion }),
    }

    return payload
  }

  const payload: CommonCourseThemeBody = {
    title: form.title,
    courseChapter: chapterIri,
    test: resolveInternalCourseThemeTest(form, internalTheme),
    hoursTime: parseFormHoursToMinutes(form.hoursTime),
    courseTheme: '',
    ...(controlQuestion === undefined ? {} : { controlQuestion }),
  }

  return payload
}

const submitInternalTheme = async (params: {
  form: ThemeFormValues
  isEdit: boolean
  chapterIri: string
  internalTheme: ThemeFormModalProps['internalTheme'] | undefined
  courseIri: string | undefined
  fileIri: string | undefined
  postScorm: ScormUploadParams['postScorm']
  putScorm: ScormUploadParams['putScorm']
  postCourseTheme: (
    body: CommonCourseThemeBody
  ) => {
    unwrap: () => Promise<unknown>
  }
  putCourseTheme: (
    body: UpdateCourseThemeBody
  ) => {
    unwrap: () => Promise<unknown>
  }
}): Promise<void> => {
  if (!params.isEdit && !params.courseIri) return
  if (params.isEdit && !params.internalTheme) return

  const shouldUploadScorm =
    !!params.fileIri && (!params.isEdit || !!params.internalTheme?.courseTheme?.id)

  const uploadResult = shouldUploadScorm
    ? await uploadScormAndGetTheme({
        form: params.form,
        fileIri: params.fileIri!,
        isEdit: params.isEdit,
        scormId: params.internalTheme?.courseTheme?.id,
        courseIri: params.courseIri,
        postScorm: params.postScorm,
        putScorm: params.putScorm,
      })
    : {}

  if (params.isEdit) {
    const payload = buildInternalCourseThemePayload({
      form: params.form,
      chapterIri: params.chapterIri,
      internalTheme: params.internalTheme,
      isEdit: true,
    })

    await params.putCourseTheme(payload as UpdateCourseThemeBody).unwrap()
    return
  }

  const scormThemeIri = uploadResult.scormThemeIri
  if (!scormThemeIri) return

  const payload = buildInternalCourseThemePayload({
    form: params.form,
    chapterIri: params.chapterIri,
    internalTheme: params.internalTheme,
    isEdit: false,
  }) as CommonCourseThemeBody

  await params.postCourseTheme({
    ...payload,
    courseTheme: scormThemeIri,
  }).unwrap()
}

const ThemeFormModal: FC<ThemeFormModalProps> = ({
  isEdit = false,
  chapterIri,
  handleCloseModal,
  internalCourseMode = false,
  theme,
  internalTheme,
  courseIri,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<
    FetchBaseQueryError | SerializedError | undefined
  >(undefined)

  const [postScorm] = usePostScormMutation()
  const [putScorm] = usePutScormMutation()
  const [postCourseTheme, postData] = usePostCourseThemeMutation()
  const [putCourseTheme, putData] = usePutCourseThemeMutation()

  const additionalProps = isEdit ? putData : postData
  const modalMutationProps =
    !internalCourseMode && isEdit
      ? { ...additionalProps, isSuccess: false }
      : additionalProps

  const methods = useFormProcessor({
    defaultValues: getDefaultValues({ theme, internalTheme, internalCourseMode }),
    apiErrors: additionalProps.error || submitError,
  })
  const { clearErrors } = methods

  const fields = useThemeFormFields({ internalCourseMode, isEdit })
  const test = methods.watch('test')

  const validateControlQuestion = useCallback(
    (form: ThemeFormValues): boolean => {
      const cq = form.controlQuestion
      const hasQuestion = !!cq?.question?.trim()
      const options = cq?.options ?? []
      const filledOptions = options.filter((o) => o.text.trim())
      const hasEnoughOptions = filledOptions.length >= 2

      if (!hasQuestion) {
        methods.setError('controlQuestion.question', {
          message: 'Это обязательное поле',
        })
      } else {
        clearErrors('controlQuestion.question')
      }

      if (!hasEnoughOptions) {
        methods.setError('controlQuestion.options', {
          message: 'Заполните минимум 2 варианта ответа',
        })
      } else {
        clearErrors('controlQuestion.options')
      }

      return hasQuestion && hasEnoughOptions
    },
    [clearErrors, methods]
  )

  const handleInternalSubmit = useCallback(
    (form: ThemeFormValues) => {
      if (!validateControlQuestion(form)) return

      const fileIri = form.file?.['@id']
      if (!isEdit && !fileIri) return

      setIsSubmitting(true)
      setSubmitError(undefined)

      const submitInternal = async () => {
        await submitInternalTheme({
          form,
          isEdit,
          chapterIri,
          internalTheme,
          courseIri,
          fileIri,
          postScorm,
          putScorm,
          postCourseTheme,
          putCourseTheme,
        })
      }

      void submitInternal()
        .catch((error) => {
          setSubmitError(error as FetchBaseQueryError | SerializedError)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    },
    [chapterIri, courseIri, internalTheme, isEdit, postCourseTheme, postScorm, putCourseTheme, putScorm, validateControlQuestion]
  )

  const handleCatalogSubmit = useCallback(
    (form: ThemeFormValues) => {
      // Контрольный вопрос — только для клиентских курсов (Мои курсы).
      // В общих курсах (Витрина) его нет: не валидируем и не отправляем.
      const commonForm: CommonCourseThemeBody = {
        title: form.title,
        courseTheme: form.courseTheme?.value,
        courseChapter: chapterIri,
        test: test ?? false,
        // hoursTime хранится в минутах (см. themeHoursTime.ts / бэкенд-рекалькулятор).
        // Часы каталожной темы Витрины — целое число как есть (отображение), без
        // ×60: длительность курса считается отдельно, не из суммы часов тем.
        hoursTime: form.hoursTime ? Number(form.hoursTime) : undefined,
      }

      if (isEdit && theme) {
        void putCourseTheme({ ...commonForm, id: theme.id })
          .unwrap()
          .then(() => {
            notification.success({ message: 'Вы успешно изменили тему' })
            handleCloseModal?.()
          })
          .catch(() => undefined)
        return
      }

      void postCourseTheme(commonForm)
    },
    [chapterIri, handleCloseModal, isEdit, postCourseTheme, putCourseTheme, test, theme]
  )

  const handleSubmitForm = internalCourseMode ? handleInternalSubmit : handleCatalogSubmit

  return (
    <ThemeFormModalFrame>
      <AddNewItemModal
        {...modalMutationProps}
        isLoading={isSubmitting || additionalProps.isLoading}
        handleCloseModal={handleCloseModal}
        onSubmitForm={handleSubmitForm}
        methods={methods}
        fields={fields}
        isEdit={isEdit}
        className={`theme-form-modal${
          internalCourseMode ? '' : ' theme-form-modal--catalog'
        }`}
        titlePostfix="тему"
        successContentAddTitle="Вы успешно добавили тему"
        successContentEditTitle="Вы успешно изменили тему"
        withSaveAndAddMore
        contentBeforeFields={
          internalCourseMode ? (
            <div id="theme-modal-instructions">
              <InstructionList>
                <li>Вы можете загрузить темы в формате pdf или SCORM.</li>
                <li>Для корректного отображения собирайте SCORM с помощью инструмента iSpring.</li>
                <li>Тема считается пройденной, если будет дан верный ответ на контрольный вопрос. Заполните его ниже.</li>
              </InstructionList>
            </div>
          ) : undefined
        }
        contentAfterFields={
          internalCourseMode ? (
            <div id="theme-modal-control-question">
              <ControlQuestionBlock />
            </div>
          ) : undefined
        }
      />
    </ThemeFormModalFrame>
  )
}

export default ThemeFormModal
